import { useState, useEffect, useCallback } from 'react';

import { showModal } from 'modal.react.js';
import { isEmpty } from 'ramda';
import { useSelector, useDispatch } from 'react-redux';

import { MANUALLY_TRANSFER, MANUALLY_FLIGHT } from 'components/Pages/Admin/GuestPortal/Flights/AddManually/utils';
import area from 'components/shared/GlobalModalArea/area';
import AlertModal from 'components/shared/GlobalModalArea/Modal/AlertModal';
import { removeAllRelocationsThunk } from 'store/itinerariesItem/relocations/thunks';
import { closeModalAction, openModalAction } from 'store/modal';
import { modalDataSelector, isModalOpenedSelector } from 'store/modal/selectors';
import { confirmAlert } from 'utils/alert';

import { MANUALLY_MODAL, FLIGHT_TYPE, TRANSFER_TYPE, FLIGHTS_MODAL } from './constants';
import s from './index.module.css';
import { getFilteredItems } from './utils';

export function useFlights(props) {
  const {
    items,
    onSave,
    onNextStep,
    onItemChange,
    onItemRemove,
    checkPolicy = false,
    onItemBlur,
    onEdit,
    dateFormat,
  } = props;
  const dispatch = useDispatch();
  const isModalFlightsOpened = useSelector(isModalOpenedSelector('ADD_FLIGHTS'));
  const isModalManuallyOpened = useSelector(isModalOpenedSelector('ADD_MANUALLY'));
  const isModalWhatAddOpened = useSelector(isModalOpenedSelector('WHAT_ADD_RELOCATION'));
  const modalData = useSelector(modalDataSelector);
  const [scheduledFlights, setScheduledFlights] = useState(null);
  const [needCheckLastItem, setNeedCheckLastItem] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const isModalOpen = isModalFlightsOpened || isModalManuallyOpened || isModalWhatAddOpened;

  const getCurrentItem = (itemIndex) => items[itemIndex];
  const getModalTypeAndData = (itemIndex) => {
    const currentItem = getCurrentItem(itemIndex);
    if (!currentItem) return;
    if (currentItem.type === FLIGHT_TYPE && currentItem.fromFsService)
      return { type: FLIGHTS_MODAL, data: { itemIndex, currentItem } };
    if (currentItem.type === FLIGHT_TYPE && !currentItem.fromFsService)
      return { type: MANUALLY_MODAL, data: { itemIndex, formType: MANUALLY_FLIGHT, currentItem } };
    if (currentItem.type === TRANSFER_TYPE)
      return { type: MANUALLY_MODAL, data: { itemIndex, formType: MANUALLY_TRANSFER, currentItem } };
    return null;
  };

  const closeModal = () => {
    dispatch(closeModalAction());
  };

  const openWhatAddModal = (itemIndex) => {
    dispatch(openModalAction({ type: 'WHAT_ADD_RELOCATION', data: { itemIndex } }));
  };

  const getValues = (itemIndex, newData) => {
    const currentItem = getCurrentItem(itemIndex);
    return { ...currentItem, ...newData };
  };

  const handleItemChange = ({ itemIndex, newData }) => {
    onItemChange(itemIndex, getValues(itemIndex, newData));
  };

  const handleItemBlur = ({ itemIndex, newData }) => {
    onItemBlur && onItemBlur(itemIndex, getValues(itemIndex, newData));
  };

  const handleItemRemove = (id) => {
    confirmAlert({
      confirmButtonText: 'Delete',
      onConfirm: async () => {
        await onItemRemove(id);
      },
      title: 'Are you sure you want to delete this data?',
    });
  };

  const handleOpenEditModal = (itemIndex) => {
    const modalTypeAndData = getModalTypeAndData(itemIndex);
    dispatch(
      openModalAction({
        type: modalTypeAndData?.type,
        data: modalTypeAndData?.data,
      }),
    );
  };

  const handleItemEdit = async (item) => {
    setSubmitting(true);
    await onEdit(item);
    setSubmitting(false);
  };

  const handleCheckLastItem = () => {
    setNeedCheckLastItem(true);
    setTimeout(() => setNeedCheckLastItem(false), 0);
  };

  const handleSaveData = async (values = {}) => {
    const { itemIndex, newItem } = values;

    setNeedCheckLastItem(false);
    const getData = () => {
      if (itemIndex !== undefined && newItem && !isEmpty(newItem)) {
        const beforeNewItem = items.slice(0, itemIndex);
        const afterNewItem = items.slice(itemIndex + 1);
        const collection = [...beforeNewItem, ...newItem, ...afterNewItem];
        return { relocations: getFilteredItems(collection) };
      }
      if (itemIndex === undefined && newItem && !isEmpty(newItem)) {
        return { relocations: newItem };
      }
      return { relocations: getFilteredItems(items) };
    };
    const data = getData();

    try {
      setSubmitting(true);
      await onSave(data);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!onNextStep) return;
    try {
      setSubmitting(true);
      await onNextStep({ relocations: getFilteredItems(items) });
    } finally {
      setSubmitting(false);
    }
  };

  const handleFlightsClear = () =>
    showModal(area, (res) => (
      <AlertModal
        headerText="Confirmation"
        confirmButtonText="Yes, Delete"
        closeButtonText="Cancel"
        footerClassName={s.alertModalButtons}
        res={res}
        onAccept={async () => {
          const relocations = await dispatch(removeAllRelocationsThunk());
          try {
            setSubmitting(true);
            await onSave({ relocations });
          } finally {
            setSubmitting(false);
          }
          res();
        }}
        bodyText={<div className={s.bodyText}>Are you sure you want to clear all Flights?</div>}
      />
    ));

  const highlightItem = (itemElement) => {
    itemElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
    itemElement.classList.add('active');
    setTimeout(() => itemElement.classList.remove('active'), 4000);
  };

  const checkLastItem = useCallback(() => {
    const currentLastItem = [...items][items.length - 1];
    const lastAddedItem = [...items].sort((a, b) => new Date(a?.createdAt) - new Date(b?.createdAt))[items.length - 1];
    const isNewItemLast = lastAddedItem?.id === currentLastItem?.id;
    const lastElem = document.querySelector(`[data-id="${lastAddedItem?.id}"]`);
    if (!isNewItemLast && lastElem) highlightItem(lastElem);
  }, [items]);

  const needCheckEditedItem = (id) => {
    const currentElement = document.querySelector(`[data-id="${id}"]`);
    if (currentElement) highlightItem(currentElement);
  };

  const agentBlockedContentClick = (blocked) => {
    if (blocked) {
      confirmAlert({
        confirmButtonText: 'Okay!',
        onConfirm: () => null,
        title: (
          <>
            This was added by your agent. <br /> Please contact him or her directly to make changes.
          </>
        ),
        showCancel: false,
      });
    }
  };

  useEffect(() => {
    if (needCheckLastItem) checkLastItem();
  }, [needCheckLastItem, checkLastItem]);

  return {
    items,
    modalData,
    isModalOpen,
    closeModal,
    handleItemChange,
    handleItemRemove,
    handleFlightsClear,
    getCurrentItem,
    isModalFlightsOpened,
    isModalManuallyOpened,
    setScheduledFlights,
    scheduledFlights,
    handleSaveData,
    submitting,
    handleOpenEditModal,
    isModalWhatAddOpened,
    openWhatAddModal,
    handleSubmit,
    handleCheckLastItem,
    checkPolicy,
    agentBlockedContentClick,
    handleItemBlur,
    handleItemEdit,
    needCheckEditedItem,
    dateFormat,
  };
}
